<template>
  <div class="application-component">
    <div class="application-component-title image-extra-title">
      <span>附图提取</span>
      <el-button
        v-if="!readOnly && list?.length"
        size="small"
        type="primary"
        :loading="analyzing"
        @click="handleAnalyze"
      >
        {{ hasAnalysis ? '重新识图' : '识图分析' }}
      </el-button>
    </div>
    <div
      class="application-component-content"
      :class="{ 'application-component-content--compact': compact }"
      :style="{ fontSize }"
      v-loading="!readOnly && (!data || analyzing)"
      element-loading-text="正在思考中..."
    >
      <div class="image-item" v-for="(item, index) in list" :key="item.id || index">
        <div class="image-item-value">
          <el-image
            v-if="item.fileUrl"
            :style="{width: '100%', height: imageHeight}"
            :src="item.fileUrl"
            fit="scale-down"
            @click="handleClick(index)"
            @error="handleError(index)"
          />
          <div class="image-item-value-placeholder" :style="{width: '100%', height: imageHeight}" v-else>
            <el-icon><Picture /></el-icon>
          </div>
        </div>
        <div class="image-item-name">
          {{ item.figureNo || ('图' + numberToChinese(index + 1)) }}
          <el-button size="small" @click="handleQuote(item)">引用</el-button>
          <el-button size="small" type="primary" @click="handleDownload(item)" v-if="errorIndex.includes(index)">下载</el-button>
        </div>
        <p v-if="item.suggestedDrawingDescription" class="image-item-desc">{{ item.suggestedDrawingDescription }}</p>
        <p v-else-if="item.description" class="image-item-desc">{{ item.description }}</p>
      </div>
    </div>
    <el-image-viewer
      v-if="showPreview"
      :url-list="list.map(item => item.fileUrl).filter(Boolean)"
      show-progress
      teleported
      :initial-index="previewIndex"
      @close="showPreview = false"
    />
  </div>
</template>

<script setup>
import { Picture } from '@element-plus/icons-vue'
import { ElMessage } from 'element-plus'
import { computed, onMounted, ref, watch } from 'vue'
import { useGlobalStore } from '@/store/modules/global'
import { numberToChinese, jsonParse } from '@/utils'
import { fetchGenerateDrawingAnalysis } from '@/api/writing'
import { mergeAttachmentWithAnalysis } from '@/views/writing/step4/step4Drawings'

const globalStore = useGlobalStore()

const props = defineProps({
  readOnly: {
    type: Boolean,
    default: false,
  },
  projectId: {
    type: String,
    default: '',
  },
  /** 嵌入 Step2 网格时为 true：缩小图片高度，保证描述可见 */
  compact: {
    type: Boolean,
    default: false,
  },
  data: {
    type: Object,
    default: () => null,
  },
})

const imageHeight = ref()
const showPreview = ref(false)
const previewIndex = ref(0)
const fontSize = computed(() => `${globalStore.fontSize}px`)
const errorIndex = ref([])
const analyzing = ref(false)

const analysisDocResult = computed(() => {
  const raw = props.data?.docResult
  if (!raw) return null
  return typeof raw === 'string' ? jsonParse(raw) : raw
})

const hasAnalysis = computed(() => {
  const figures = analysisDocResult.value?.figures
  return Array.isArray(figures) && figures.length > 0
})

const list = computed(() => {
  if (props.readOnly) {
    return [{ id: 1, fileName: '示例图片一', url: '' }]
  }
  if (!props.data) return null
  return mergeAttachmentWithAnalysis(props.data.attachment, props.data.docResult)
})

const handleClick = (index) => {
  if (props.readOnly) return
  showPreview.value = true
  previewIndex.value = index
}

const handleQuote = (file) => {
  if (props.readOnly) return
  globalStore.setAIVisible(true)
  setTimeout(() => {
    globalStore.setQuoteAIChat({ title: '附图提取', image: file.fileUrl })
  }, 500)
}

const handleDownload = (file) => {
  window.open(file.fileUrl, '_blank')
}

const handleError = (index) => {
  errorIndex.value.push(index)
}

const handleAnalyze = async () => {
  if (!props.projectId || analyzing.value) return
  analyzing.value = true
  try {
    await fetchGenerateDrawingAnalysis(props.projectId)
    ElMessage.success('附图识图分析完成')
  } catch {
    ElMessage.error('附图识图分析失败')
  } finally {
    analyzing.value = false
  }
}

onMounted(() => {
  updateImageHeight()
})

watch(
  () => props.compact,
  () => updateImageHeight(),
)

const updateImageHeight = () => {
  const container = document.querySelector('.application-component-content')
  const width = container?.clientWidth || 320
  const maxHeight = props.compact ? 120 : 250
  imageHeight.value = Math.min((width - 44) * (props.compact ? 0.55 : 0.7), maxHeight) + 'px'
}
</script>

<style scoped lang="scss">
@import './common.scss';

.image-extra-title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.image-item {
  max-width: 500px;
  margin: 0 auto;
  &:not(:first-child) {
    margin-top: 10px;
  }
  .image-item-value {
    width: 100%;
    overflow: hidden;
    border-radius: 20px;
    border: 1px solid #E8E9EA;
    :deep(.el-image) {
      display: block;
      width: 100%;
      height: 100%;
    }
  }
  .image-item-value-placeholder {
    width: 100%;
    overflow: hidden;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 20px;
    border: 1px solid #E8E9EA;
    :deep(.el-icon) {
      font-size: 50px;
      color: #999;
    }
  }
  .image-item-name {
    margin-top: 10px;
    font-size: inherit;
    color: #000;
    text-align: center;
    :deep(.el-button) {
      margin-left: 8px;
      border-radius: 12px;
    }
  }
  .image-item-desc {
    margin: 8px 0 0;
    font-size: 0.92em;
    color: #64748b;
    line-height: 1.55;
    text-align: left;
    flex-shrink: 0;
  }
}

.application-component-content--compact {
  .image-item {
    margin-top: 0;
    &:not(:first-child) {
      margin-top: 16px;
      padding-top: 12px;
      border-top: 1px dashed #e8ecf0;
    }
  }
  .image-item-desc {
    display: block;
    max-height: none;
    overflow: visible;
  }
}
</style>
